import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { decodePolyline } from "../utils/decodePolyline";

export const useMapboxMap = (trackerLink) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const bikerMarker = useRef(null);
  const [directionData, setDirectionData] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const handleDirectionUpdate = (data) => {
    setDirectionData(data);
    
    if (data.polyline && map.current && mapLoaded) {
      try {
        const source = map.current.getSource("direction-route");
        if (source) {
          source.setData({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: decodePolyline(data.polyline),
            },
          });
        }
      } catch (error) {
        console.error("Error updating polyline:", error);
      }
    }
  };

  const handleBikerLocationUpdate = (data) => {
    if (!map.current || !data.location) {
      return;
    }

    const { latitude, longitude } = data.location;

    if (!bikerMarker.current) {
      bikerMarker.current = new mapboxgl.Marker({ color: "#f59e0b" })
        .setLngLat([longitude, latitude])
        .setPopup(new mapboxgl.Popup().setText("Biker Location"))
        .addTo(map.current);
    } else {
      bikerMarker.current.setLngLat([longitude, latitude]);
    }

    map.current.flyTo({
      center: [longitude, latitude],
      zoom: 14,
      duration: 1000,
    });
  };

  const initializeMap = (options = {}) => {
    if (!trackerLink || !mapContainer.current) return;

    const decodedLink = decodeURIComponent(trackerLink);
    console.log("📍 Map tracker link:", decodedLink);

    const urlParams = new URLSearchParams(decodedLink.split("?")[1]);
    const origin = urlParams.get("origin")?.split(",").map(Number);
    const destination = urlParams.get("destination")?.split(",").map(Number);

    if (!origin || !destination) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [(origin[1] + destination[1]) / 2, (origin[0] + destination[0]) / 2],
      zoom: 13,
      ...options,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      }),
      "top-right"
    );

    if (options.fullscreen) {
      map.current.addControl(new mapboxgl.FullscreenControl(), "top-right");
    }

    map.current.on("load", () => {
      new mapboxgl.Marker({ color: "#10b981", scale: 1.2 })
        .setLngLat([origin[1], origin[0]])
        .setPopup(new mapboxgl.Popup().setText("🏪 Pickup Location"))
        .addTo(map.current);

      new mapboxgl.Marker({ color: "#ef4444", scale: 1.2 })
        .setLngLat([destination[1], destination[0]])
        .setPopup(new mapboxgl.Popup().setText("🏠 Delivery Location"))
        .addTo(map.current);

      map.current.addSource("traffic", {
        type: "vector",
        url: "mapbox://mapbox.mapbox-traffic-v1",
      });

      map.current.addLayer(
        {
          id: "traffic-layer",
          source: "traffic",
          "source-layer": "traffic",
          type: "line",
          paint: {
            "line-color": [
              "match",
              ["get", "congestion"],
              "low",
              "#00cc00",
              "moderate",
              "#ffcc00",
              "heavy",
              "#ff6600",
              "severe",
              "#cc0000",
              "#999999"
            ],
            "line-width": 2,
            "line-opacity": 0.7,
          },
        },
        "waterway-label"
      );

      map.current.addSource("direction-route", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [[origin[1], origin[0]], [destination[1], destination[0]]],
          },
        },
      });

      map.current.addLayer({
        id: "direction-route",
        type: "line",
        source: "direction-route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#3b82f6",
          "line-width": 4,
          "line-opacity": 0.8,
        },
      });

      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([origin[1], origin[0]]);
      bounds.extend([destination[1], destination[0]]);
      map.current.fitBounds(bounds, { padding: 50 });
      
      setMapLoaded(true);
    });

    return () => {
      if (map.current) map.current.remove();
    };
  };

  return {
    mapContainer,
    map,
    directionData,
    setDirectionData,
    handleDirectionUpdate,
    handleBikerLocationUpdate,
    initializeMap,
    mapLoaded,
  };
};
